import * as React from 'react';
import NavItems from './NavItems';
import { UserStore, FilterStore } from '../../stores';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import { NavLink } from 'react-router-dom';
import {Auth, NotificationService, UserDataService} from '../../services';
import { Survey, SurveyVersion, Organization, Team, SurveyReportType, SecondaryReport } from '../../models';
import './SideNav.css';
import { ImagePreview } from '..';
import {Roles, StorageKeys} from '../../stores/Constants';

interface SideNavMenuProps {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    open: boolean;
    notificationService: NotificationService;
}

interface SideNavMenuState {
    open: boolean;
    showOtherReports: boolean;
}

@inject('UserStore', 'FilterStore') @observer
export class SideNavMenu extends React.Component<SideNavMenuProps, SideNavMenuState> {
    private userService = new UserDataService(this.props.notificationService);

    @computed get username() {
        return `${this.props.UserStore!.userInfo.firstName} ${this.props.UserStore!.userInfo.lastName}`;
    }

    @computed get organizationName() {
        return `${this.props.UserStore!.userInfo!.organization!.name}`;
    }

    @computed get organization() {
        return this.props.UserStore!.userInfo!.organization;
    }

    @computed get organizations() {
        return (this.props.FilterStore && (this.props.FilterStore.organizations || [])) || [];
    }

    @computed get surveys() {
        return (this.props.FilterStore && (this.props.FilterStore.surveys || [])) || [];
    }

    @computed get surveyVersions() {
        return (this.props.FilterStore && (this.props.FilterStore.surveyVersions || [])) || [];
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get teams() {
        return this.props.FilterStore!.teams || [];
    }

    @computed get secondaryReports() {
        return this.props.FilterStore!.secondaryReports || [];
    }

    @computed get surveyReportTypes() {
        return this.props.FilterStore!.surveyReportTypes || [];
    }

    @computed get surveyReportType() {
        return this.props.FilterStore!.surveyReportType || undefined;
    }

    @computed get selectedOrg() {
        return this.props.FilterStore!.organization || undefined;
    }

    @computed get selectedSurvey() {
        return this.props.FilterStore!.survey || undefined;
    }

    @computed get selectedSurveyVersion() {
        return this.props.FilterStore!.surveyVersion || undefined;
    }

    @computed get selectedTeamId() {
        return this.props.FilterStore!.teamId;
    }

    @computed get avatar() {
        return this.props.UserStore!.userInfo.avatar;
    }

    @computed get filteredOrganization() {
        return this.props.FilterStore!.organization;
    }

    @computed get isBeingImpersonated() {
        return this.props.UserStore!.userInfo.isBeingImpersonated;
    }

    constructor(props: any) {
        super(props);

        this.state = {
            open: this.props.open,
            showOtherReports: false
        };
    }

    protected getInitialOrganization(): Organization {
        if (this.isBeingImpersonated) return this.organization;
        let orgString = sessionStorage.getItem(StorageKeys.NavBarSelectedOrganization);
        if (!orgString) orgString = localStorage.getItem(StorageKeys.NavBarSelectedOrganization);
        if (!!orgString) return JSON.parse(orgString);
        return this.organization;
    }
    protected setInitialOrganization(organization: Organization) {
        const orgString = JSON.stringify(organization);
        localStorage.setItem(StorageKeys.NavBarSelectedOrganization, orgString);
        sessionStorage.setItem(StorageKeys.NavBarSelectedOrganization, orgString);
        this.clearInitialTeamId();
    }
    protected getInitialTeamId(): number {
        const initialTeamIdString = sessionStorage.getItem(StorageKeys.NavBarSelectedTeamId)
            || localStorage.getItem(StorageKeys.NavBarSelectedTeamId);
        if (!!initialTeamIdString) {
            const initialTeamId = JSON.parse(initialTeamIdString);
            if (this.teams.map(t => t.id).includes(initialTeamId)) {
                return JSON.parse(initialTeamIdString);
            }
        }

        let initialTeamId = 0;
        if (this.canViewAllFormerEmployees()) initialTeamId = -300;
        if (this.canViewAllCandidates()) initialTeamId = -200;
        if (this.canViewAllUsers()) initialTeamId = -100;

        if (initialTeamId == 0 && this.teams.length > 0) initialTeamId = this.teams[0].id;

        return initialTeamId;
    }
    protected setInitialTeamId(teamId: number) {
        const teamString = JSON.stringify(teamId);
        localStorage.setItem(StorageKeys.NavBarSelectedTeamId, teamString);
        sessionStorage.setItem(StorageKeys.NavBarSelectedTeamId, teamString);
    }
    protected clearInitialTeamId() {
        localStorage.removeItem(StorageKeys.NavBarSelectedTeamId);
        sessionStorage.removeItem(StorageKeys.NavBarSelectedTeamId);
    }

    public async componentDidMount() {
        await this.props.FilterStore!.getOrganizations(this.getInitialOrganization());
        await this.loadInitialTeam();
    }

    private async orgChange(e: any) {
        var orgId = Number(e.target.value);
        var org = this.organizations.find((o: Organization) => {
            return o.id === orgId;
        });
        if (org !== undefined) {
            await this.props.FilterStore!.setOrganization(org, this.canViewAllUsers() ? -100 : 0);
            await this.loadInitialTeam();
            this.setInitialOrganization(org);
        }
    }

    private async loadInitialTeam() {
        if (this.getInitialTeamId() <= 0) {
            await this.props.FilterStore!.setTeamID(this.getInitialTeamId());
        }
        else {
            const team = this.teams.find(team => team.id === this.getInitialTeamId());
            if (team) await this.props.FilterStore!.setTeam(team);
        }
    }

    private teamChange(e: any) {
        var teamId = Number(e.target.value);
        var team = this.teams.find((t: Team) => {
            return t.id === teamId;
        });
        if (team !== undefined && this.props.FilterStore) {
            this.props.FilterStore.setTeam(team);
        } else if (this.props.FilterStore) {
            this.props.FilterStore.setTeamID(teamId);
        }
        this.setInitialTeamId(teamId);
    }

    private surveyChange(e: any) {
        var surveyId = Number(e.target.value);
        var survey = this.surveys.find((o: Survey) => {
            return o.id === surveyId;
        });
        if (survey !== undefined) {
            this.props.FilterStore!.setSurvey(survey);
        }
    }

    private surveyVersionChange(e: any) {
        var surveyVersionId = Number(e.target.value);
        var surveyVersion = this.surveyVersions.find((o: SurveyVersion) => {
            return o.id === surveyVersionId;
        });
        if (surveyVersion !== undefined) {
            this.props.FilterStore!.setSurveyVersion(surveyVersion);
        }
    }

    private surveyReportTypeChange(e: any) {
        var surveyReportTypeId = Number(e.target.value);
        if (!this.selectedSurvey) {
            return;
        }

        var surveyReportType = this.selectedSurvey.surveyReportTypes.find((o: SurveyReportType) => {
            return o.id === surveyReportTypeId;
        });

        if (surveyReportType !== undefined) {
            this.props.FilterStore!.setSurveyReportType(surveyReportType);
        }
    }

    shouldComponentUpdate(nextProps: Readonly<SideNavMenuProps>, nextState: Readonly<{}>, nextContext: any) {
        // force updates so the active styling stays in sync
        return true;
    }

    private toggleOpen() {
        window.dispatchEvent(new Event('side-menu-toggle'));
        this.setState({ open: !this.state.open });
    }

    private getLogo() {
        const baseLogoComponent = (
            <div className="Rectangle-Copy-4 text-center">
                <img className="pondera-logo-img" src="/images/ponderalogo.png" />
            </div>
        );

        if (this.filteredOrganization) {
            if (this.filteredOrganization.showLogoInNav) {
                const logoUrl = this.filteredOrganization.logo;
                let classString = 'Rectangle-Copy-4 text-center';
                if (this.filteredOrganization.useWhiteBackgroundInNav) { classString = `${classString} white`; }

                return (
                    <div className={classString}>
                        <img className="client-logo-img" src={logoUrl} />
                    </div>
                );
            } else {
                return baseLogoComponent;
            }

        } else if (this.organization) {
            if (this.organization.showLogoInNav) {
                const logoUrl = this.organization.logo;
                let classString = 'Rectangle-Copy-4 text-center';
                if (this.organization.useWhiteBackgroundInNav) { classString = `${classString} white`; }

                return (
                    <div className={classString}>
                        <img className="client-logo-img" src={logoUrl} />
                    </div>
                );
            } else {
                return baseLogoComponent;
            }

        }

        return baseLogoComponent;
    }

    stopImpersonation = () => {
        const shouldContinue = confirm(`Are you sure you want to stop impersonating ${this.username}?`);

        if (shouldContinue) {
            this.userService.stopImpersonation();
        }
    }

    canViewAllUsers = () => {
        return !this.props.UserStore!.userRoleIn(['manager', 'user'])
            || this.userPermissions.indexOf('viewAllUsers') > -1;
    }

    canViewAllCandidates = () => {
        return !this.props.UserStore!.userRoleIn(['manager', 'user'])
            || this.userPermissions.indexOf('viewAllCandidates') > -1
    }

    canViewAllFormerEmployees = () => {
        return !this.props.UserStore!.userRoleIn(['manager', 'user'])
            || this.userPermissions.indexOf('viewAllFormerEmployees') > -1
    }

    public render() {
        let imageUrl = '/images/userIcon.png';
        if (this.avatar) {
            imageUrl = `data:image/png;base64,${this.avatar}`;
        }

        let logoComponent = this.getLogo();

        let toggleIcon;
        if (this.state.open) {
            toggleIcon = <span className="glyphicon glyphicon-menu-hamburger" />;
        } else {
            toggleIcon = <span className="glyphicon glyphicon-menu-hamburger" />;
        }

        let profileActions;
        if (!this.isBeingImpersonated) {
            profileActions = (
                <td className="text-right">
                    <NavLink to="/userSettings">
                        <span className="glyphicon glyphicon-cog" />
                    </NavLink>
                    <NavLink to="/logout" activeClassName="logout">
                        <span className="glyphicon glyphicon-log-out" />
                    </NavLink>
                </td>
            );
        } else {
            profileActions = (
                <td className="text-center" style={{ minWidth: '60px', maxWidth: '80px' }}>
                    <button className="btn btn-danger" onClick={this.stopImpersonation}><span className="glyphicon glyphicon-sunglasses" /></button>
                </td>
            );
        }

        let defaultTeams = [];

        if (this.canViewAllUsers()) {
            defaultTeams.push(<option key="team-select-all-users" value={-100}>All Users</option>);
        }
        if (this.canViewAllCandidates()) {
            defaultTeams.push(<option key="team-select-candidates" value={-200}>All Candidates</option>);
        }
        if (this.canViewAllFormerEmployees()) {
            defaultTeams.push(<option key="team-select-former-employees" value={-300}>All Former Employees</option>);
        }

        return (
            <div className={`side-nav-container ${this.state.open ? 'open' : ''}`}>
                {this.teams && <div id={`teams-count-${this.teams.length}`} />}
                {this.surveyReportType && <div id={`surveyReportType-${this.surveyReportType.id}`} />}

                <div className="side-nav-toggle" onClick={(e) => this.toggleOpen()}>
                    {toggleIcon}
                </div>
                <div className="side-nav-toggle sub" onClick={(e) => this.toggleOpen()}>
                    <div className="sub-content" />
                </div>
                <div className="side-nav">
                    <div className="navbar">
                        <ul className="nav navbar-nav">
                            <div className="nav-items-container">
                                <div className="nav-items-container-inner">
                                    <div className="nav-items">
                                        {(defaultTeams.length + (this.teams ? this.teams.length : 0)) > 1 && <div className="form-group selector">
                                            <h3>Team</h3>
                                            <div className="select-container">
                                                <select className="form-control" onChange={(e) => this.teamChange(e)} value={this.selectedTeamId}>
                                                    {defaultTeams}
                                                    {
                                                        this.teams && this.teams.length > 0 &&
                                                        this.teams.sort((a: Team, b: Team) => {
                                                            if (a.name > b.name) {
                                                                return 1;
                                                            } else if (a.name < b.name) {
                                                                return -1;
                                                            }
                                                            return 0;
                                                        }).map((team: Team, index: number) => {
                                                            return <option key={`team-global-${index}`} value={team.id}>{team.name}</option>;
                                                        })
                                                    }
                                                </select>
                                            </div>
                                        </div>}

                                        <NavItems />

                                        {this.secondaryReports.length > 0 && <div>
                                            <div className="side-nav-header" onClick={(e) => { this.setState({ showOtherReports: !this.state.showOtherReports}); }}>
                                                <div className="side-nav-header-text">
                                                    <h3>Other</h3>
                                                </div>
                                                <div className="side-nav-header-toggle">
                                                    {
                                                        this.state.showOtherReports && <span className="glyphicon glyphicon-triangle-bottom" />
                                                    }
                                                    {
                                                        !this.state.showOtherReports && <span className="glyphicon glyphicon-triangle-right" />
                                                    }
                                                </div>
                                            </div>
                                            {
                                                this.state.showOtherReports && this.secondaryReports.map((report: SecondaryReport, i: number) => {
                                                    return <li key={`navitem-${i}`}>
                                                        <NavLink to={`/report/${report.id}`} activeClassName="active">
                                                            <span className="fas fa-list" /> {report.name}
                                                        </NavLink>
                                                    </li>;
                                                })
                                            }
                                        </div>}

                                        {this.organizations.length > 1 && this.userRole !== 'user' && <div className="form-group selector">
                                            <h3>Organization</h3>
                                            <div className="select-container">
                                                {this.selectedOrg && <select className="form-control" onChange={(e) => this.orgChange(e)} value={this.selectedOrg.id}>
                                                    {
                                                        this.organizations.map((organization: Organization, index: number) => {
                                                            return <option key={`organization-global-${index}`} value={organization.id}>{organization.name}</option>;
                                                        })
                                                    }
                                                </select>}
                                            </div>
                                        </div>}

                                        {this.surveys.length > 1 && ['superAdmin', 'siteAdmin', 'ponderaAssociate', 'organizationAdmin', 'manager'].includes(this.userRole) && <div className="form-group selector">
                                            <h3>Survey</h3>
                                            <div className="select-container">
                                                {this.selectedSurvey && <select className="form-control" value={this.selectedSurvey.id} onChange={(e) => this.surveyChange(e)}>
                                                    {
                                                        this.surveys.map((survey: Survey, index: number) => {
                                                            return <option key={`survey-global-${index}`} value={survey.id}>{survey.name}</option>;
                                                        })
                                                    }
                                                </select>}
                                            </div>
                                        </div>}

                                        {this.surveyVersions.length > 1 && ['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <div className="form-group selector">
                                            <h3>Survey Version</h3>
                                            <div className="select-container">
                                                {this.selectedSurveyVersion && <select className="form-control" value={this.selectedSurveyVersion.id} onChange={(e) => this.surveyVersionChange(e)}>
                                                    {
                                                        this.surveyVersions.map((surveyVersion: SurveyVersion, index: number) => {
                                                            const { id, active, published, nickname, version } = surveyVersion;
                                                            return <option key={`survey-global-${index}`} value={id}>
                                                                #{version} {nickname ? nickname : ''}{active ? ' (active)' : ''}{!published ? ' *' : ''}
                                                            </option>;
                                                        })
                                                    }
                                                </select>}
                                            </div>
                                        </div>}
                                        
                                        {this.surveyReportTypes && this.surveyReportTypes.length > 1 && this.canViewSurveyReportTypes() && <div className="form-group selector">
                                            <h3>Survey Report Type</h3>
                                            <div className="select-container">
                                                {this.surveyReportType && <select className="form-control" value={this.surveyReportType.id} onChange={(e) => this.surveyReportTypeChange(e)}>
                                                    {
                                                        this.surveyReportTypes.map((surveyReportType: SurveyReportType, index: number) => {
                                                            return <option key={`survey-global-${index}`} value={surveyReportType.id}>
                                                                {surveyReportType.name}
                                                            </option>;
                                                        })
                                                    }
                                                </select>}
                                            </div>
                                        </div>}

                                    </div>
                                </div>
                            </div>
                            {logoComponent}
                            <div className="profile Rectangle-3-Copy-2">
                                <table style={{ width: '100%' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ width: '50px' }}>
                                                <div className="profile-image">
                                                    <ImagePreview maxWidth={50} maxHeight={50} imageUrl={imageUrl} />
                                                </div>
                                            </td>
                                            <td>
                                                <div className="profile-menu">
                                                    <div className="org-name"><span>{this.organizationName}</span></div>
                                                    {this.username}
                                                </div>
                                            </td>
                                            {profileActions}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    private canViewSurveyReportTypes() {
        return ['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) || this.userPermissions.indexOf('canViewSurveyReportTypes') > -1;
    }
}



// WEBPACK FOOTER //
// ./src/components/navigation/SideNavMenu.tsx